import React from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Cloud,
  Code2,
  Database,
  Headphones,
  Layers3,
  ReceiptText,
  Rocket,
  Settings2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  UserRoundCog,
  UsersRound,
  Workflow,
  Zap,
} from "lucide-react";

import FAQSection from "./FAQSection";


const services = [
  {
    icon: Cloud,
    title: "SaaS Product Development",
    category: "Cloud Software Development",
    short:
      "Build scalable cloud-based SaaS products designed around your business model, users and operational requirements.",
    description:
      "OjMee Technology designs and develops SaaS platforms from product planning and architecture to development, APIs, dashboards, subscriptions, deployment and ongoing improvement.",
    features: [
      "Multi-User SaaS Platforms",
      "Subscription & Plan Management",
      "Cloud-Based Architecture",
      "Admin & User Dashboards",
    ],
  },

  {
    icon: Database,
    title: "ERP Software Development",
    category: "Enterprise Business Software",
    short:
      "Centralize departments, workflows, business data and operational processes through a connected ERP platform.",
    description:
      "We develop modular ERP software that helps organizations connect inventory, sales, purchasing, operations, reporting and other important business functions within one centralized system.",
    features: [
      "Inventory Management",
      "Sales & Purchase",
      "Business Operations",
      "Reports & Analytics",
    ],
  },

  {
    icon: UserRoundCog,
    title: "CRM Software Development",
    category: "Sales & Customer Management",
    short:
      "Manage leads, customers, sales pipelines and follow-ups through a CRM designed around your business workflow.",
    description:
      "Our CRM solutions help businesses organize customer information, improve sales visibility, manage opportunities and build structured processes for customer engagement and follow-up.",
    features: [
      "Lead Management",
      "Customer Database",
      "Sales Pipeline",
      "Follow-Up Management",
    ],
  },

  {
    icon: UsersRound,
    title: "HRMS Software Development",
    category: "HR & Workforce Management",
    short:
      "Digitize employee management, attendance, leave, payroll and everyday HR operations through one connected platform.",
    description:
      "We build HRMS solutions that centralize employee information and automate repetitive workforce processes while giving HR teams better operational visibility.",
    features: [
      "Employee Management",
      "Attendance & Leave",
      "Payroll Workflows",
      "HR Reports & Analytics",
    ],
  },

  {
    icon: ReceiptText,
    title: "Billing & Invoicing Software",
    category: "Financial Workflow Software",
    short:
      "Create connected billing and invoicing systems for customers, products, quotations, payments and business transactions.",
    description:
      "We develop billing and invoicing platforms for businesses that need structured transaction management, GST workflows, payment tracking, customer records and operational reporting.",
    features: [
      "GST & Non-GST Invoices",
      "Quotation Management",
      "Payment Tracking",
      "Customer & Product Records",
    ],
  },

  {
    icon: Bot,
    title: "AI & Business Automation",
    category: "Intelligent Automation",
    short:
      "Use AI and workflow automation to reduce repetitive tasks and improve the efficiency of everyday business operations.",
    description:
      "We help businesses integrate intelligent automation into practical workflows, including data processing, internal operations, customer interactions, reporting and software-assisted decision processes.",
    features: [
      "Workflow Automation",
      "AI Integrations",
      "Process Automation",
      "Smart Business Tools",
    ],
  },

  {
    icon: Cloud,
    title: "Cloud & API Development",
    category: "Backend & Integration Engineering",
    short:
      "Connect applications, databases and third-party platforms through secure and scalable cloud APIs.",
    description:
      "We design backend services and APIs that allow web applications, mobile apps, SaaS products, payment systems and external platforms to exchange information efficiently.",
    features: [
      "REST API Development",
      "Third-Party Integrations",
      "Cloud Connectivity",
      "Authentication & Access Control",
    ],
  },

  {
    icon: Layers3,
    title: "Software Integration",
    category: "Connected Business Systems",
    short:
      "Connect existing software, APIs, databases, payment platforms and operational tools into a unified digital workflow.",
    description:
      "Our integration services help businesses reduce disconnected processes by enabling applications and services to exchange data through structured and reliable integrations.",
    features: [
      "API Integration",
      "Payment Gateway Integration",
      "Database Integration",
      "Third-Party Services",
    ],
  },

  {
    icon: Smartphone,
    title: "Mobile App Development",
    category: "Android & iOS Applications",
    short:
      "Build connected mobile applications for customers, employees, partners and business operations.",
    description:
      "We develop mobile applications with modern interfaces, secure APIs, cloud connectivity and scalable backend systems for Android and iOS experiences.",
    features: [
      "Android & iOS Apps",
      "Business Applications",
      "API Integration",
      "Push Notifications",
    ],
  },

  {
    icon: Code2,
    title: "Web Application Development",
    category: "Modern Web Platforms",
    short:
      "Develop fast, responsive and scalable web applications designed for business workflows and digital services.",
    description:
      "From customer portals and management dashboards to complex business applications, we create modern web platforms focused on usability, performance and maintainability.",
    features: [
      "Business Web Applications",
      "Customer Portals",
      "Admin Dashboards",
      "Responsive UI/UX",
    ],
  },

  {
    icon: Settings2,
    title: "Custom Software Development",
    category: "Business-Specific Solutions",
    short:
      "Turn unique operational requirements into software designed specifically around your business processes.",
    description:
      "When off-the-shelf software does not fit your workflow, we design custom applications, modules, dashboards and systems around your organization's specific requirements.",
    features: [
      "Custom Business Software",
      "Workflow Management",
      "Role-Based Systems",
      "Custom Reporting",
    ],
  },

  {
    icon: Headphones,
    title: "Software Support & Scaling",
    category: "Maintenance & Product Growth",
    short:
      "Keep your software reliable, secure and ready for changing business requirements after launch.",
    description:
      "Our support services help maintain and improve software through monitoring, issue resolution, performance optimization, feature enhancements and infrastructure scaling.",
    features: [
      "Application Maintenance",
      "Performance Optimization",
      "Feature Enhancements",
      "Technical Support",
    ],
  },
];

const advantages = [
  {
    icon: Layers3,
    title: "Business-First Architecture",
    text:
      "We understand the business workflow first and then design the software architecture around real operational requirements.",
  },

  {
    icon: ShieldCheck,
    title: "Security Conscious",
    text:
      "Authentication, authorization, access control and data protection are considered throughout the software development lifecycle.",
  },

  {
    icon: Zap,
    title: "Performance & Scalability",
    text:
      "Our solutions are designed with application performance, maintainability and long-term scalability in mind.",
  },

  {
    icon: Headphones,
    title: "Long-Term Support",
    text:
      "Our relationship can continue after deployment through maintenance, optimization, integrations and product improvements.",
  },
];

const process = [
  {
    number: "01",
    title: "Business Discovery",
    text:
      "We understand your business model, operational challenges, users, workflows, objectives and existing technology environment.",
  },

  {
    number: "02",
    title: "Product Planning",
    text:
      "We define the required modules, user journeys, technical architecture, integrations, database structure and implementation roadmap.",
  },

  {
    number: "03",
    title: "UI/UX & Architecture",
    text:
      "We structure the product experience and engineering foundation so the software remains practical, maintainable and ready to evolve.",
  },

  {
    number: "04",
    title: "Development & Integration",
    text:
      "Our team develops the application, backend services, APIs, dashboards and required integrations through a structured engineering process.",
  },

  {
    number: "05",
    title: "Testing & Deployment",
    text:
      "The software is tested across important workflows and prepared for deployment to the appropriate cloud or production environment.",
  },

  {
    number: "06",
    title: "Support & Scale",
    text:
      "After launch, the platform can continue evolving through monitoring, improvements, integrations, additional modules and scalability work.",
  },
];

const solutionTypes = [
  {
    icon: Cloud,
    title: "SaaS Platforms",
    text:
      "Subscription-based cloud software for customers, teams and organizations.",
  },

  {
    icon: Database,
    title: "Enterprise Systems",
    text:
      "ERP, CRM, HRMS and centralized operational software for growing organizations.",
  },

  {
    icon: Bot,
    title: "AI Automation",
    text:
      "Intelligent workflows and automation for repetitive business processes.",
  },

  {
    icon: Smartphone,
    title: "Connected Applications",
    text:
      "Web and mobile applications connected through secure APIs and cloud services.",
  },

  {
    icon: Workflow,
    title: "Business Automation",
    text:
      "Software workflows designed to reduce manual work and improve operational efficiency.",
  },

  {
    icon: Layers3,
    title: "System Integration",
    text:
      "Connect software, databases, APIs and third-party platforms into one ecosystem.",
  },
];

export default function Services() {
  return (
    <main className="w-full overflow-hidden">

      <section className="relative w-full px-4 py-5 sm:px-6 lg:py-10">

        <div className="pointer-events-none absolute left-1/2 top-20 -z-10 h-[550px] w-[850px] -translate-x-1/2 rounded-full bg-pink-600/10 blur-[160px]" />


        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-5xl text-center">

            {/* BADGE */}

            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-5 py-2.5">

              <Sparkles className="h-4 w-4 text-pink-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-200">
                SaaS & Software Development Services
              </span>

            </div>


            {/* H1 */}

            <h1 className="text-4xl font-black leading-tight sm:text-6xl lg:text-7xl">

              SaaS & Business Software

              <br />

              <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                Development Services
              </span>

            </h1>


            {/* DESCRIPTION */}

            <p className="mx-auto mt-7 max-w-4xl text-base leading-8 text-slate-400 sm:text-lg">

              OjMee Technology helps businesses design, develop and scale
              cloud-based SaaS products, ERP, CRM, HRMS, billing and
              invoicing platforms, AI automation, APIs, mobile applications
              and custom business software.

            </p>


            <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-slate-500 sm:text-base">

              From product planning and software architecture to development,
              integration, deployment and ongoing support, we build
              technology around practical business requirements.

            </p>


            {/* CTA */}

            <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">

              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 font-bold text-white shadow-lg shadow-pink-500/20 transition hover:scale-105"
              >
                Discuss Your Software Project

                <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
              </Link>


              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
              >
                Explore Our Work
              </Link>

            </div>

          </div>

        </div>

      </section>


      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-10 sm:px-6 lg:py-20">

        <div className="pointer-events-none absolute right-0 top-1/3 -z-10 h-[450px] w-[450px] rounded-full bg-violet-600/10 blur-[140px]" />


        <div className="mx-auto max-w-7xl">

          <div className="max-w-4xl">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

              <Settings2 className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                What We Build
              </span>

            </div>


            <h2 className="text-3xl font-black sm:text-5xl">

              End-to-End Software Solutions

              <br />

              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                For Modern Businesses
              </span>

            </h2>


            <p className="mt-5 max-w-3xl text-sm leading-8 text-slate-500 sm:text-base">

              Whether you are launching a new SaaS product, replacing manual
              processes or modernizing existing business software, our team
              can design and develop a solution around your operational and
              technical requirements.

            </p>

          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-3">

            {services.map((service, index) => {
              const Icon = service.icon;

              return (

                <article
                  key={service.title}
                  className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-7 transition duration-500 hover:-translate-y-2 hover:border-pink-400/30 hover:bg-white/[0.045]"
                >

                  <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-pink-500/10 opacity-0 blur-[80px] transition duration-500 group-hover:opacity-100" />


                  <div className="relative">

                    <div className="flex items-start justify-between">

                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15 ring-1 ring-white/5">

                        <Icon className="h-6 w-6 text-pink-300" />

                      </div>


                      <span className="text-xs font-bold tracking-widest text-slate-700">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                    </div>

                    <div className="mt-6">

                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-pink-300/70">
                        {service.category}
                      </span>


                      <h3 className="mt-2 text-xl font-black">
                        {service.title}
                      </h3>

                    </div>

                    <p className="mt-3 text-sm font-medium leading-7 text-slate-300">
                      {service.short}
                    </p>


                    <p className="mt-4 text-sm leading-7 text-slate-500">
                      {service.description}
                    </p>

                    <div className="mt-6 space-y-2.5 border-t border-white/10 pt-5">

                      {service.features.map((feature) => (

                        <div
                          key={feature}
                          className="flex items-center gap-2 text-xs text-slate-400"
                        >

                          <CheckCircle2 className="h-4 w-4 shrink-0 text-pink-400" />

                          <span>{feature}</span>

                        </div>

                      ))}

                    </div>

                    <Link
                      to="/contact"
                      className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-pink-300 transition hover:text-pink-200"
                    >
                      Discuss This Service

                      <ArrowRight className="h-4 w-4" />
                    </Link>

                  </div>

                </article>

              );
            })}

          </div>

        </div>

      </section>

      <section className="w-full px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-3xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">

              <Layers3 className="h-4 w-4 text-pink-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                Software Capabilities
              </span>

            </div>


            <h2 className="text-3xl font-black sm:text-5xl">

              Technology Designed Around

              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                {" "}
                Business Workflows
              </span>

            </h2>


            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">

              We combine product engineering, cloud technology, integrations
              and automation to create software that supports real
              operational requirements.

            </p>

          </div>


          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {solutionTypes.map((item) => {
              const Icon = item.icon;

              return (

                <div
                  key={item.title}
                  className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-400/25"
                >

                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/10 to-violet-500/10">

                    <Icon className="h-5 w-5 text-pink-300" />

                  </div>


                  <h3 className="mt-5 text-lg font-black">
                    {item.title}
                  </h3>


                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {item.text}
                  </p>

                </div>

              );
            })}

          </div>

        </div>

      </section>

      <section className="w-full border-y border-white/5 bg-white/[0.015] px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-3xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">

              <Rocket className="h-4 w-4 text-pink-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                Our Engineering Approach
              </span>

            </div>


            <h2 className="text-3xl font-black sm:text-5xl">

              More Than Software

              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                {" "}
                Development
              </span>

            </h2>


            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">

              Our approach combines business understanding, product
              engineering and long-term technical thinking to create
              software that can continue evolving after launch.

            </p>

          </div>


          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

            {advantages.map((item) => {
              const Icon = item.icon;

              return (

                <div
                  key={item.title}
                  className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-400/30"
                >

                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-500/10">

                    <Icon className="h-5 w-5 text-violet-300" />

                  </div>


                  <h3 className="mt-5 text-lg font-black">
                    {item.title}
                  </h3>


                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {item.text}
                  </p>

                </div>

              );
            })}

          </div>

        </div>

      </section>

      <section className="w-full px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">

            <div className="lg:sticky lg:top-28">

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

                <Workflow className="h-4 w-4 text-violet-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                  Software Development Process
                </span>

              </div>


              <h2 className="text-3xl font-black sm:text-5xl">

                From Business Idea to

                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  Working Software
                </span>

              </h2>


              <p className="mt-6 text-sm leading-8 text-slate-500 sm:text-base">

                Our structured software development process starts with
                understanding the business problem and continues through
                architecture, engineering, deployment and ongoing product
                improvement.

              </p>


              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-bold transition hover:border-pink-400/30 hover:bg-white/10"
              >
                Start Your Software Project

                <ArrowRight className="h-4 w-4" />
              </Link>

            </div>

            <div className="space-y-4">

              {process.map((item) => (

                <div
                  key={item.number}
                  className="group flex gap-5 rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:border-pink-400/25 hover:bg-white/[0.04] sm:p-7"
                >

                  <div className="shrink-0 text-2xl font-black text-pink-400/60">
                    {item.number}
                  </div>


                  <div>

                    <h3 className="text-xl font-black">
                      {item.title}
                    </h3>


                    <p className="mt-2 text-sm leading-7 text-slate-500">
                      {item.text}
                    </p>

                  </div>

                </div>

              ))}

            </div>

          </div>

        </div>

      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-24">

        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[450px] w-[850px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/[0.07] blur-[160px]" />


        <div className="mx-auto max-w-5xl">

          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-pink-500/[0.07] via-white/[0.025] to-violet-500/[0.07] px-6 py-12 text-center sm:px-10 lg:px-16 lg:py-16">

            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pink-500/10 blur-[100px]" />

            <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-violet-500/10 blur-[100px]" />


            <div className="relative">

              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-300">
                Build With OjMee Technology
              </span>


              <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-black leading-tight sm:text-5xl">

                Have a Business Challenge That

                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  Software Can Solve?
                </span>

              </h2>


              <p className="mx-auto mt-6 max-w-3xl text-sm leading-8 text-slate-400 sm:text-base">

                Tell us about your workflow, business requirements or SaaS
                product idea. We can help plan and develop a scalable software
                solution around your operational needs.

              </p>


              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">

                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 text-sm font-bold text-white shadow-xl shadow-pink-500/20 transition hover:scale-105"
                >
                  Discuss Your Requirement

                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </Link>


                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 text-sm font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
                >
                  Explore SaaS Products
                </Link>

              </div>

            </div>

          </div>

        </div>

      </section>

      <section className="relative w-full px-4 py-10 sm:px-6 lg:py-0">
        <FAQSection />
      </section>

    </main>
  );
}